import React from 'react';
import { useNavigate } from 'react-router-dom';
import { consoleTokens, Kbd } from '@components/ui/console';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useReportsAccess } from '../hooks/useReportsAccess';
import {
  useDeleteSavedReportView,
  useRecentRuns,
  useReportDefinitions,
  useSavedReportViews,
} from '../store/hooks';
import type { RecentRun, SavedReportView, SavedViewParams } from '../types';
import { asText } from '../utils/consoleAdapter';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: consoleTokens.fainter,
      fontSize: '0.625rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      mb: 0.5,
      mt: 3,
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

const relativeTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(Math.round((Date.now() - then) / 60000), 0);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const describeRunParams = (params: Record<string, unknown>): string => {
  const parts: string[] = [];
  if (params.start_date && params.end_date)
    parts.push(`${asText(params.start_date)} → ${asText(params.end_date)}`);
  if (params.basis) parts.push(asText(params.basis).replace(/_/g, ' '));
  const groupBy = Array.isArray(params.group_by) ? (params.group_by as string[]) : [];
  if (groupBy.length > 0) parts.push(`by ${groupBy.join('→')}`);
  return parts.join(' · ');
};

const rowHoverSx = {
  alignItems: 'center',
  borderBottom: `1px solid ${consoleTokens.rowLine}`,
  cursor: 'pointer',
  display: 'flex',
  gap: 2,
  px: 1.5,
  '&:hover': { bgcolor: consoleTokens.bandBg },
} as const;

/**
 * Reports home as a console list: recents first (one click back into a run),
 * then the catalog ordered by 30-day usage, then saved views.
 */
export const ReportsHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { hasAccess: canSeeReports, isLoading: accessLoading } = useReportsAccess();
  const { enqueueSnackbar } = useSnackbar();

  const { data: definitionsData, isLoading } = useReportDefinitions();
  const { data: recentRunsData } = useRecentRuns({ limit: 8 });
  const { data: savedViewsData, isLoading: savedViewsLoading } = useSavedReportViews({});
  const [deleteSavedView] = useDeleteSavedReportView();

  if (!accessLoading && !canSeeReports) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">
          You don&apos;t have permission to view reports. Ask a practice administrator to grant
          report access for your role.
        </Alert>
      </Container>
    );
  }

  const openRecentRun = (run: RecentRun) => {
    const params = new URLSearchParams();
    if (run.params.start_date) params.set('start', String(run.params.start_date));
    if (run.params.end_date) params.set('end', String(run.params.end_date));
    if (run.params.basis) params.set('basis', String(run.params.basis));
    if (Array.isArray(run.params.group_by))
      params.set('group', (run.params.group_by as string[]).join(','));
    navigate(`/reports-analytics/${run.report_key}?${params.toString()}`);
  };

  const handleDeleteSavedView = async (view: SavedReportView) => {
    try {
      await deleteSavedView({ viewId: view.report_saved_view_id }).unwrap();
      enqueueSnackbar('Saved view deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not delete the saved view', { variant: 'error' });
    }
  };

  const reports = [...(definitionsData?.reports ?? [])].sort(
    (a, b) => (b.run_count_30d ?? 0) - (a.run_count_30d ?? 0) || a.title.localeCompare(b.title),
  );
  const recentRuns = recentRunsData?.runs ?? [];
  const savedViews = savedViewsData?.views ?? [];

  return (
    <Box sx={{ bgcolor: '#FFFFFF', color: consoleTokens.ink, minHeight: '100%' }}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="h1" sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
            Reports
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center', color: consoleTokens.dim, fontSize: '0.75rem' }}
          >
            <Kbd>⌘K</Kbd>
            <span>jump anywhere</span>
          </Stack>
        </Stack>

        {recentRuns.length > 0 && (
          <>
            <SectionLabel>Recent</SectionLabel>
            <Box sx={{ borderTop: `1px solid ${consoleTokens.line}` }}>
              {recentRuns.map((run, index) => (
                <Box
                  key={`${run.report_key}-${index}`}
                  onClick={() => openRecentRun(run)}
                  sx={{ ...rowHoverSx, height: 56 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                      {run.title}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        color: consoleTokens.dim,
                        fontFamily: consoleTokens.mono,
                        fontSize: '0.6875rem',
                      }}
                    >
                      {describeRunParams(run.params)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    sx={{
                      color: consoleTokens.fainter,
                      fontFamily: consoleTokens.mono,
                      fontSize: '0.6875rem',
                    }}
                  >
                    {relativeTime(run.ran_at)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        <SectionLabel>Reports</SectionLabel>
        {isLoading ? (
          <Stack spacing={1} sx={{ pt: 1 }}>
            {[0, 1].map(index => (
              <Skeleton key={index} variant="rounded" height={56} />
            ))}
          </Stack>
        ) : (
          <Box sx={{ borderTop: `1px solid ${consoleTokens.line}` }}>
            {reports.map(report => (
              <Box
                key={report.key}
                data-testid={`report-card-${report.key}`}
                onClick={() => navigate(`/reports-analytics/${report.key}`)}
                sx={{ ...rowHoverSx, height: 64 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {report.title}
                  </Typography>
                  <Typography noWrap sx={{ color: consoleTokens.dim, fontSize: '0.75rem' }}>
                    {report.description}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                  {(report.run_count_30d ?? 0) > 0 && (
                    <Typography
                      sx={{
                        color: consoleTokens.dim,
                        fontFamily: consoleTokens.mono,
                        fontSize: '0.6875rem',
                      }}
                    >
                      {report.run_count_30d}× this month
                    </Typography>
                  )}
                  {report.last_run_at && (
                    <Typography
                      sx={{
                        color: consoleTokens.fainter,
                        fontFamily: consoleTokens.mono,
                        fontSize: '0.6875rem',
                      }}
                    >
                      last {relativeTime(report.last_run_at)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        )}

        <SectionLabel>Saved views</SectionLabel>
        {savedViewsLoading ? (
          <CircularProgress size={20} />
        ) : savedViews.length === 0 ? (
          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.75rem' }}>
            No saved views yet. Run a report and save its configuration to rerun it later.
          </Typography>
        ) : (
          <Box sx={{ borderTop: `1px solid ${consoleTokens.line}` }}>
            {savedViews.map(view => {
              const params = view.params as SavedViewParams;
              const windowLabel = params.snapshot
                ? 'current snapshot'
                : params.window?.kind === 'relative_days'
                  ? `rolling ${params.window.days}d`
                  : params.window?.kind === 'absolute'
                    ? `${params.window.start} → ${params.window.end}`
                    : 'default window';
              return (
                <Box
                  key={view.report_saved_view_id}
                  onClick={() =>
                    navigate(
                      `/reports-analytics/${view.report_key}?view=${view.report_saved_view_id}`,
                    )
                  }
                  sx={{ ...rowHoverSx, height: 48 }}
                >
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {view.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: consoleTokens.dim,
                      fontFamily: consoleTokens.mono,
                      fontSize: '0.6875rem',
                    }}
                  >
                    {view.report_key} · {windowLabel}
                    {params.group_by && params.group_by.length > 0
                      ? ` · by ${params.group_by.join('→')}`
                      : ''}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <IconButton
                    size="small"
                    aria-label={`Delete saved view ${view.name}`}
                    onClick={event => {
                      event.stopPropagation();
                      void handleDeleteSavedView(view);
                    }}
                    sx={{ color: consoleTokens.fainter }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};
